import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Grid, Button, Typography, Box, Link ,Tooltip,Avatar  } from "@mui/material";
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import Image from "react-bootstrap/Image";
import {
  useJsApiLoader,
  StandaloneSearchBox,
  GoogleMap,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { PlaceRounded } from "@mui/icons-material";

import image from "../assets/images/placeholder.jpg";
import { useTranslation } from 'react-i18next';
import { companyService } from "../app/services";
import { Rate } from "antd";
import dayjs from "dayjs";
import 'dayjs/locale/en';

const LIBRARIES = ["places"];


const mapOptions = {
  styles: [
    {
      featureType: "all",
      elementType: "geometry",
      stylers: [{ "saturation": -100 }, { "lightness": 10 }]
    },
    {
      featureType: "water",
      elementType: "geometry.fill",
      stylers: [{ "color": "#d3eaf7" }, { "lightness": 10 }]
    },
    {
      featureType: "landscape.man_made",
      elementType: "geometry.fill",
      stylers: [{ "color": "#f0f4f8" }]
    },
    {
      featureType: "poi",
      elementType: "geometry.fill",
      stylers: [{ "color": "#e6f0f7" }]
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ "color": "#ffffff" }, { "weight": 1.2 }]
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ "color": "#e9ecef" }, { "weight": 1.5 }]
    },
    {
      featureType: "transit",
      elementType: "geometry",
      stylers: [{ "visibility": "simplified" }, { "color": "#e6e9ed" }]
    },
    {
      featureType: "administrative",
      elementType: "labels.text.fill",
      stylers: [{ "color": "#5a6778" }, { "weight": 0.5 }]
    },
    {
      featureType: "administrative.neighborhood",
      elementType: "labels.text",
      stylers: [{ "visibility": "off" }]
    },
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ "visibility": "off" }]
    }
  ],
  disableDefaultUI: true,
  zoomControl: true,

  fullscreenControl: false,
  streetViewControl: false,
  gestureHandling: "cooperative",
  minZoom: 3,
  maxZoom: 18,
  restriction: {
    latLngBounds: {
      north: 85,
      south: -85,
      east: 180,
      west: -180
    },
    strictBounds: false
  }
};

const FETCH_THRESHOLD = 0.005; // ~500 meters

const MapPicker = ({
  Lat,
  Lng,
  Address,
  selectedProfessionalType,
  handleViewScheduleClick
}) => {
    const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyB_j8NP_ZfkMFGHNxqsCL_pPhEIBlBp9As",
    libraries: LIBRARIES,
  });
  const { t } = useTranslation();
  const [autocomplete, setAutocomplete] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [focusedMarker, setFocusedMarker] = useState(null);
  const [lastFetchCenter, setLastFetchCenter] = useState({ lat: null, lng: null });
  const [position, setPosition] = useState({ lat: Lat, lng: Lng });
  const [address, setAddress] = useState(Address);
  const [registeredProfessionals, setRegisteredProfessionals] = useState([]);
  const [detailedBusinesses, setDetailedBusinesses] = useState([]);
  const mapRef = useRef(null);



  // Fetch registered professionals
  useEffect(() => {
    companyService
      .getCompaniesByCategoryAndLocationAndCompanyid(
        selectedProfessionalType.name,
        { Lat: position.lat, Lng: position.lng }
      )
      .then(setRegisteredProfessionals)
      .catch(console.error);
  }, [selectedProfessionalType, position]);

  // Debounced fetch of nearby businesses
  const fetchNearbyBusinesses = useCallback(async (location) => {
    if (!location || !mapRef.current) return;

    try {
      const { Place, SearchNearbyRankPreference } = await google.maps.importLibrary("places");
      const request = {
            locationRestriction: {
              center: location,
              radius: 500,
            },
            maxResultCount: 5,
            rankPreference: SearchNearbyRankPreference.POPULARITY,
            fields: ["id", "displayName", "location", "businessStatus"],
          };

      const { places } = await Place.searchNearby(request);
      const detailedPlaces = await Promise.all(
        places.map(async (placeSummary) => {
          const place = new Place({ id: placeSummary.id });
          await place.fetchFields({
            fields: ["displayName", "formattedAddress", "businessStatus", "websiteURI", "location"],
          });
          let lat, lng;

              // Case 1: location is a LatLng object
              if (typeof place.location.lat === 'function') {
                lat = place.location.lat();
                lng = place.location.lng();
              }
              // Case 2: location is a plain object
              else if (typeof place.location.lat === 'number') {
                lat = place.location.lat;
                lng = place.location.lng;
              }
              // Fallback
              else {
                lat = null;
                lng = null;
              }


          return {
            place_id: place.id,
            name: place.displayName,
            formatted_address: place.formattedAddress,
            business_status: place.businessStatus,
            website: place.websiteURI,
            geometry: place.location,
            position: { lat, lng }
          };
        })
      );

      setDetailedBusinesses(detailedPlaces);
    } catch (error) {
      console.error("Failed to fetch nearby businesses:", error);
    }
  }, [selectedProfessionalType]);

  // Handle map bounds changes with threshold check
  const handleBoundsChanged = useCallback(() => {
    if (!mapRef.current) return;

    const center = mapRef.current.getCenter();
    const newCenter = { lat: center.lat(), lng: center.lng() };

    if (
      !lastFetchCenter.lat ||
      Math.abs(newCenter.lat - lastFetchCenter.lat) > FETCH_THRESHOLD ||
      Math.abs(newCenter.lng - lastFetchCenter.lng) > FETCH_THRESHOLD
    ) {
      setLastFetchCenter(newCenter);
      setPosition(newCenter);
      fetchNearbyBusinesses(newCenter);
    }
  }, [lastFetchCenter, fetchNearbyBusinesses]);

  // Handle place selection from search box
  const handlePlaceSelect = useCallback(() => {
    if (!autocomplete) return;

    const place = autocomplete.getPlaces()[0];
    if (place?.geometry?.location) {
      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };

      setAddress(place.formatted_address);
      setPosition(location);
      setLastFetchCenter(location);
      fetchNearbyBusinesses(location);
    }
  }, [autocomplete, fetchNearbyBusinesses]);

  // Merge professional data
  const mergedBusinesses = useMemo(() => {

    const registered = registeredProfessionals.map(p => ({
      ...p,
      isRegistered: true,
      type: 'registered',
      position: p.coordinate,
    }));

    const businesses = detailedBusinesses.map(b => ({
      ...b,
      isRegistered: false,
      type: 'google'
    }));
    return [...registered, ...businesses];
  }, [registeredProfessionals, detailedBusinesses]);

  // Map event handlers
  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
    setFocusedMarker(marker);
  };

  const handleListItemClick = (professional) => {
    setFocusedMarker(professional);
  };

  if (!isLoaded) return <div>{t("Loading...")}</div>;

  return (
    <Grid container spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
      {/* Professional List Panel */}
      <Grid item md={7} sx={{  height: { xs: '400px', md: '537px' },  display: 'flex', flexDirection: 'column' , overflow: 'auto' }}>
        {mergedBusinesses.length > 0 ? (
          <Box component="ul" sx={{ p: 0, m: 0 }}>
            {mergedBusinesses.map((professional, index) => (
              <ProfessionalListItem
                key={`${professional.type}-${index}`}
                professional={professional}
                onClick={handleListItemClick}
                onViewSchedule={handleViewScheduleClick}
                t={t}
                isFocused={focusedMarker === professional}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {t("No professionals found in your area.")}
          </Typography>
        )}
      </Grid>

      {/* Map Panel */}
      <Grid item xs={12} md={5} sx={{ height: { xs: '400px', md: '537px' }, width: '100%', position: 'relative' }}>
        <Box sx={{ position: 'absolute', marginTop: 2, marginLeft: 2, top: 10, left: 10, zIndex: 1 }}>
          <StandaloneSearchBox onLoad={setAutocomplete} onPlacesChanged={handlePlaceSelect}>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'white', borderRadius: 1, px: 1, boxShadow: 2, border: '1px solid #ddd', }}>
              <PlaceRounded color="primary" />
              <input
                type="text"
                placeholder={t("Enter address")}
                style={{ border: 'none', padding: '8px', width: '300px', outline: 'none' }}
              />
            </Box>
          </StandaloneSearchBox>
        </Box>

        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={position}
          zoom={16}
          options={mapOptions}
          onLoad={(map) => {
            mapRef.current = map;
            fetchNearbyBusinesses(position);
          }}
          onIdle={handleBoundsChanged}
        >
          <Marker
            position={position}
            icon="http://maps.google.com/mapfiles/ms/icons/green-dot.png"
          />

          {mergedBusinesses.map((professional, index) => (
            <Marker
              key={`marker-${index}`}
              position={{
                lat: parseFloat(professional.position.lat),
                lng: parseFloat(professional.position.lng)
              }}
              icon={
                professional === focusedMarker
                  ? "http://maps.google.com/mapfiles/ms/icons/red-dot.png"
                  : professional.isRegistered
                    ? "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                    : "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
              }
              onClick={() => handleMarkerClick(professional)}
            />
          ))}

          {selectedMarker && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedMarker.position.lat),
                lng: parseFloat(selectedMarker.position.lng)
              }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle1">{selectedMarker.name}</Typography>
                <Typography variant="body2">
                  {selectedMarker.address || selectedMarker.formatted_address}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    handleViewScheduleClick(selectedMarker.isRegistered ? selectedMarker : null);
                    setSelectedMarker(null);
                  }}
                  sx={{ mt: 1 }}
                >
                  {selectedMarker.isRegistered
                    ? t("View Disponibilities")
                    : t("Not Available")}
                </Button>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      </Grid>
    </Grid>
  );
};

// Professional List Item Component
const ProfessionalListItem = React.memo(({
  professional,
  onClick,
  onViewSchedule,
  t,
  isFocused
}) => {
  const statusColor = professional.isRegistered ? 'success.main' : 'error.main';
  const statusText = professional.isRegistered
    ? t("AppointmentForm.Bookable")
    : t("AppointmentForm.NotAvailable");

  const getNext7Days = () => {
    const days = [];
    const today = dayjs();
    for (let i = 0; i < 7; i++) {
      days.push(today.add(i, 'day'));
    }
    return days;
  };


  return (
    <Box
      component="li"
      onClick={() => onClick(professional)}
      sx={{
        border: '1px solid',
        borderColor: isFocused ? 'primary.main' : 'divider',
        borderRadius: 2,
        p: 2,
        mb: 2,
        display: 'flex',
        bgcolor: professional.isRegistered ? 'background.paper' : 'grey.100',
        cursor: 'pointer',
        boxShadow: professional.isRegistered ? 2 : 0,
        opacity: professional.isRegistered ? 1 : 0.6,
        transition: 'border-color 0.3s, box-shadow 0.3s, opacity 0.3s'
      }}
    >

      {/* Image & Status Badge */}
      <Box sx={{
        top: -20, mr: 2, position: 'relative'
      }}>
        <Box
          sx={{
            position: "relative",
            top: 10,
            left: -20,
            bgcolor: "#003a4d",
            color: "white",
            offset: 999,
            fontSize: 12,
            fontWeight: 600,
            overflow: "hidden",
            px: 1.5,
            py: 0.25,
            borderTopLeftRadius: 0,
            borderBottomRightRadius: 6,
            borderTopRightRadius: 6,
            borderBottomLeftRadius: 0,
            boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          {statusText}
        </Box>

        <Image
          src={professional.imageUrl?.trim() || image}
          alt={professional.name || "Placeholder"}
          width={150}
          height={150}
          style={{
            borderRadius: 8,
            objectFit: 'cover',
            filter: professional.isRegistered ? 'none' : 'grayscale(100%)',
          }}
        />
      </Box>

      {/* Professional Details */}
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {professional.name}
          </Typography>
        </Box>

        <Typography  component="div"
          variant="body2"
          sx={{
            color: 'text.secondary',
            bgcolor: 'green',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {professional.address || professional.formatted_address}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2, flexShrink: 0 }}>
            <LocationOnOutlinedIcon fontSize="small" color="action" />
            <span>
              {professional?.distanceFromCurrent?.crowflies
                ? `${(parseFloat(professional.distanceFromCurrent.crowflies)).toFixed(1)} km`
                : ''}
            </span>
          </Box>
        </Typography>

     
{/* Services Icons in One Row */}
    {Array.isArray(professional.companyServices) && professional.companyServices.length > 0 && (
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {professional.companyServices.map((service, index) => (
          <Tooltip key={index} title={service.name} arrow>
            <Avatar
              alt={service.name}
              src={service.icon || undefined}
              onError={(e) => (e.currentTarget.src = '/default-icon.png')}
              sx={{
                width: 36,
                height: 36,
                bgcolor: service.icon ? 'grey.100' : 'primary.light',
                border: '1px solid #ddd',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 2,
                  transform: 'scale(1.05)',
                  transition: '0.2s',
                },
              }}
            >
              {!service.icon && (service.name?.charAt(0) || '?')}
            </Avatar>
          </Tooltip>
        ))}
      </Box>
    )}

        {professional.rating && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 ,
            bgcolor: 'yellow'}}>
            <Rate value={professional.rating} disabled allowHalf />
            <Typography variant="caption" sx={{ ml: 1 }}>
              ({professional.user_ratings_total || 0})
            </Typography>
          </Box>
        )}

        {/* 7-Day Availability Indicator */}
 {professional.isRegistered && (<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column',
            bgcolor: 'orange' }}>
          {/* Left: 7-Day Availability */}
          <Box sx={{ alignItems: 'flex-start' }}>
            {Array.isArray(professional.firstAvailableSlots) && (() => {
              const next7Days = getNext7Days();
              const hasAvailabilityIn7Days = professional.firstAvailableSlots.some(slot =>
                next7Days.some(day => dayjs(slot.startDate).isSame(day, 'day'))
              );

              if (hasAvailabilityIn7Days) {
                return (
                  <Box sx={{ display: 'flex' }}>
                    {next7Days.map((day, index) => {
                      const hasAvailability = professional.firstAvailableSlots.some(slot =>
                        dayjs(slot.startDate).isSame(day, 'day')
                      );

                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mx: 0.5,
                          }}
                        >
                          <Typography variant="body2" sx={{ mb: 0.5, fontSize: 12, color: 'text.primary' }}>
                            {day.format('ddd')}
                          </Typography>

                          <Box
                            height={20}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              borderRadius: 1,
                              bgcolor: hasAvailability ? 'success.main' : 'grey.300',
                              color: hasAvailability ? 'white' : 'text.secondary',
                              transition: 'background-color 0.3s',
                              fontSize: 14,
                              fontWeight: 'bold',
                              width: 40,
                            }}
                          >
                            <Typography variant="caption" sx={{ fontSize: 10 }}>
                              {day.format('D MMM.')}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              } else {
                const nextSlot = professional.firstAvailableSlots
                  .map(slot => dayjs(slot.startDate))
                  .find(d => d.isAfter(dayjs()));

                return (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    {nextSlot
                      ? t("AppointmentForm.NextAvailabilityIs", { date: nextSlot.format("dddd D MMMM") })
                      : t("AppointmentForm.NoAvailability")}
                  </Typography>
                );
              }
            })()}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',  
              alignItems: 'center',
              gap: 1,
              mt: 1, 
            }}
          >
            <Link
              href={`/professionals/${professional.id}`}
              underline="hover"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: 14, color: 'black' }}
            >
              Go to professional page
            </Link>

            <Button
              variant="contained"
              color="primary"
              className="submit-btn"
              onClick={(e) => {
                e.stopPropagation();
                onViewSchedule(professional.isRegistered ? professional : null);
              }}
              size="small"
            >
              Book
            </Button>
          </Box>
        </Box>)}
      </Box>
    </Box>
  );
});

export default MapPicker;



{/* Availability Button */ }
{/* <Button
      variant="contained"
      color={professional.isRegistered ? "primary" : "secondary"}
      onClick={(e) => {
        e.stopPropagation();
        onViewSchedule(professional.isRegistered ? professional : null);
      }}
      sx={{ mt: 1 }}
      disabled={!professional.isRegistered}
    >
      {professional.isRegistered
        ? t("AppointmentForm.ViewDisponibilities")
        : t("AppointmentForm.NotAvailable")}
    </Button> */}
{/* {Array.isArray(professional.firstAvailableSlots) && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        {getNext7Days().map((day, index) => {
          const hasAvailability = professional.firstAvailableSlots.some(slot =>
            dayjs(slot.startDate).isSame(day, 'day')
          );

          return (
            <Box
                    key={index}
                    height={40}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      textAlign: 'center',
                      flex: 1,
                      mx: 0.5,
                      p: 0.5,
                      borderRadius: 1,
                      bgcolor: hasAvailability ? 'success.main' : 'grey.300',
                      color: hasAvailability ? 'white' : 'text.secondary',
                      transition: 'background-color 0.3s',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: 8 }}>
                      {day.format("dd")} 
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 10 }}>
                      {day.format("D")} 
                  </Box>
          );
        })}
      </Box>
    )} */}
// <Box 
//   component="li" 
//   onClick={() => onClick(professional)}
//   sx={{
//     border: '1px solid',
//     borderColor: isFocused ? 'primary.main' : 'divider',
//     borderRadius: 2,
//     p: 2,
//     mb: 2,
//     display: 'flex',
//     bgcolor: 'background.paper',
//     cursor: 'pointer',
//     boxShadow: 1,
//     transition: 'border-color 0.3s'
//   }}
// >
//   {/* Thumbnail */}
//   <Box sx={{ mr: 2, position: 'relative' }}>
//     <Box
//       component="img"
//       src={professional.imageUrl || "/placeholder.jpg"}
//       alt={professional.name}
//       sx={{ width: 100, height: 100, borderRadius: 1, objectFit: 'cover' }}
//     />
//     <Box
//       sx={{
//         position: 'absolute',
//         top: 8,
//         left: 8,
//         bgcolor: statusColor,
//         color: 'white',
//         px: 1,
//         py: 0.5,
//         borderRadius: 1,
//         fontSize: 12,
//         fontWeight: 'bold'
//       }}
//     >
//       {statusText}
//     </Box>
//   </Box>

//   {/* Details */}
//   <Box sx={{ flexGrow: 1 }}>
//     <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
//       <Typography variant="h6" sx={{ fontWeight: 600 }}>
//         {professional.name}
//       </Typography>
//     </Box>

//     <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
//       {professional.address || professional.formatted_address}
//     </Typography>

//     {professional.rating && (
//       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
//         <Rate value={professional.rating} disabled allowHalf />
//         <Typography variant="caption" sx={{ ml: 1 }}>
//           ({professional.user_ratings_total || 0})
//         </Typography>
//       </Box>
//     )}



//     <Button
//       variant="contained"
//       color={professional.isRegistered ? "primary" : "secondary"}
//       onClick={(e) => {
//         e.stopPropagation();
//         onViewSchedule(professional.isRegistered ? professional : null);
//       }}
//       sx={{ mt: 1 }}
//     >
//       {professional.isRegistered
//         ? t("AppointmentForm.ViewDisponibilities")
//         : t("AppointmentForm.NotAvailable")}
//     </Button>
//      {/* 7-Day Availability Indicator */}
//       {Array.isArray(professional.firstAvailableSlots) && (
//           <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
//             {getNext7Days().map((day, index) => {
//               const hasAvailability = professional.firstAvailableSlots.some(slot =>
//                 dayjs(slot.startDate).isSame(day, 'day')
//               );

//               return (
//                 <Box
//                   key={index}
//                   sx={{
//                     textAlign: 'center',
//                     flex: 1,
//                     mx: 0.5,
//                     p: 0.5,
//                     borderRadius: 1,
//                     bgcolor: hasAvailability ? 'success.main' : 'grey.300',
//                     color: hasAvailability ? 'white' : 'text.secondary'
//                   }}
//                 >
//                   <Typography variant="caption" sx={{ fontSize: 10 }}>
//                     {day.format("dd")}
//                   </Typography>
//                   <Typography variant="caption" sx={{ fontSize: 12 }}>
//                     {day.format("D")}
//                   </Typography>
//                 </Box>
//               );
//             })} 
//           </Box>
//         )}
//   </Box>
// </Box>


// <Box 
//   component="li" 
//   onClick={() => onClick(professional)}
//   sx={{
//     border: '1px solid',
//     borderColor: isFocused ? 'primary.main' : 'divider',
//     borderRadius: 2,
//     p: 2,
//     mb: 2,
//     display: 'flex',
//     bgcolor: 'background.paper',
//     cursor: 'pointer',
//     boxShadow: 1,
//     transition: 'border-color 0.3s'
//   }}
// >
//   {/* Thumbnail */}
//   <Box sx={{ mr: 2, position: 'relative' }}>
//     <Box
//       component="img"
//       src={professional.imageUrl || "/placeholder.jpg"}
//       alt={professional.name}
//       sx={{ width: 100, height: 100, borderRadius: 1, objectFit: 'cover' }}
//     />
//     <Box
//       sx={{
//         position: 'absolute',
//         top: 8,
//         left: 8,
//         bgcolor: statusColor,
//         color: 'white',
//         px: 1,
//         py: 0.5,
//         borderRadius: 1,
//         fontSize: 12,
//         fontWeight: 'bold'
//       }}
//     >
//       {statusText}
//     </Box>
//   </Box>

//   {/* Details */}
//   <Box sx={{ flexGrow: 1 }}>
//     <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
//       <Typography variant="h6" sx={{ fontWeight: 600 }}>
//         {professional.name}
//       </Typography>
//       <Typography variant="caption" sx={{ color: 'text.secondary' }}>
//         {/* Distance would go here */}
//       </Typography>
//     </Box>

//     <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
//       {professional.address || professional.formatted_address}
//     </Typography>

//     {professional.rating && (
//       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
//         <Rate value={professional.rating} disabled allowHalf />
//         <Typography variant="caption" sx={{ ml: 1 }}>
//           ({professional.user_ratings_total || 0})
//         </Typography>
//       </Box>
//     )}

//     <Button
//       variant="contained"
//       color={professional.isRegistered ? "primary" : "secondary"}
//       onClick={(e) => {
//         e.stopPropagation();
//         onViewSchedule(professional.isRegistered ? professional : null);
//       }}
//       sx={{ mt: 1 }}
//     >
//       {professional.isRegistered
//         ? t("AppointmentForm.ViewDisponibilities")
//         : t("AppointmentForm.NotAvailable")}
//     </Button>
//   </Box>
// </Box>
// import React, { useState, useRef, useEffect } from "react";
// import { Grid, Button, Typography } from "@mui/material";
// import {
//   LoadScript,useJsApiLoader,
//   StandaloneSearchBox,
//   GoogleMap,
//   Marker,
//   InfoWindow,
// } from "@react-google-maps/api";
// import { PlaceRounded } from "@mui/icons-material";
// import { useTranslation } from 'react-i18next';
// import { companyService } from "../app/services"; // Ensure this service is correctly set up
// import { Rate } from "antd";


// const MapPicker = ({
//   Lat,
//   Lng,
//   Address,
//   selectedProfessionalType,
//   handleViewScheduleClick
// }) => {
//   const [searchAddress, setSearchAddress] = useState("");
//   const [autocomplete, setAutocomplete] = useState(null);
//   // const [businesses, setBusinesses] = useState([]);
//   const [selectedMarker, setSelectedMarker] = useState(null);
//   const [focusedMarker, setFocusedMarker] = useState(null);
//   const [latitude, setLatitude] = useState(Lat);
//   const [longitude, setLongitude] = useState(Lng);
//   const [address, setAddress] = useState(Address);
//   const [professionalType, setProfessionalType] = useState(selectedProfessionalType);
//   const { t } = useTranslation();
//   const libraries = ["places"];  // Define libraries array outside the component
//   const { isLoaded } = useJsApiLoader({
//     googleMapsApiKey: "AIzaSyB_j8NP_ZfkMFGHNxqsCL_pPhEIBlBp9As",
//   });
//   const [registeredProfessionals, setRegisteredProfessionals] = useState([]);
//   const [detailedBusinesses, setDetailedBusinesses] = useState([]);
//  // const [mergedBusinesses, setMergedBusinesses] = useState([]);

//  const [map, setMap] = useState(null);

//   const mapRef = useRef(null);
//   const [lastFetchCenter, setLastFetchCenter] = useState({ lat: null, lng: null });
//   const FETCH_THRESHOLD = 0.005; // Adjust this threshold as needed

//   useEffect(() => {
//     // Fetch professionals from your backend
//     companyService
//       .getCompaniesByCategoryAndLocationAndCompanyid(professionalType.name, { Lat: latitude, Lng: longitude })
//       .then((company) => {
//         console.log("company.reference");
//         console.log(company[0].reference);
//         setRegisteredProfessionals(company);   
//       })
//       .catch((error) => {
//         console.error("Failed to fetch professionals:", error);
//       });
//   }, [professionalType, latitude, longitude]);

//   const fetchNearbyBusinesses = async (location) => {
//     
//     console.log("Location for nearby search:", location);
//     if (location && mapRef.current) {


//     const { Place, SearchNearbyRankPreference } = await google.maps.importLibrary("places");

//       const service = new window.google.maps.places.PlacesService(mapRef.current);

//       // Retrieve the current map bounds
//       const bounds = mapRef.current.getBounds();


//       const request = {
//         fields: ["id","displayName", "location", "businessStatus"],
//         locationRestriction: {
//           center:location,
//           radius: 500,
//         },
//         includedPrimaryTypes: [professionalType.googleTypeName],
//         maxResultCount: 5,
//         rankPreference: SearchNearbyRankPreference.POPULARITY,
//         language: "en-US",
//         region: "us",
//       };
//       const { places } = await Place.searchNearby(request);
//     // Fetch additional details for all places
//     const detailedPlaces = await Promise.all(
//       places.map(async (placeSummary) => {
//         const place = new Place({
//           id: placeSummary.id, // Use the id from searchNearby
//           requestedLanguage: "en",
//         });

//         // Fetch fields for each place
//         await place.fetchFields({
//           fields: [
//             "displayName",
//             "formattedAddress",
//              "businessStatus",
//              "websiteURI",
//              "location",
//             // "businessStatus",
//             // "website",
//             // "vicinity",
//           ],
//         });

//         return {
//           place_id: place.id,
//           name: place.displayName,
//           formatted_address: place.formattedAddress || "",
//           // rating: place.rating || null,
//           // total_ratings: place.userRatingsTotal || 0,
//           business_status: place.businessStatus || "",
//           website: place.websiteURI || null,
//           geometry: place.location || null,
//           // vicinity: place.vicinity || "",
//         };
//       })
//     );

//           // Update the state with detailed places
//           console.log("Detailed Places:", detailedPlaces);
//           setDetailedBusinesses(detailedPlaces);

//     }
//   };

//   const searchBusinessByAddress = (address) => {
//     if (address && mapRef.current) {
//       const service = new window.google.maps.places.PlacesService(mapRef.current);

//       // Use textSearch to find the business by address
//       service.textSearch(
//         {
//           query: address,
//           location: mapRef.current.getCenter(), // Optional: prioritize results near the center of the map
//           radius: 5000, // Adjust the radius if needed
//         },
//         (results, status) => {
//           if (status === window.google.maps.places.PlacesServiceStatus.OK && results.length > 0) {
//             const firstResult = results[0];
//             const request = {
//               placeId: firstResult.place_id,
//               fields: ["name", "formatted_address", "place_id", "geometry","rating"],
//             };

//             service.getDetails(request, (place, status) => {
//             // Now fetch detailed information for the first result
//             // service.getDetails(
//             //   {
//             //     placeId: firstResult.place_id,
//                 // fields: [
//                 //   "place_id",
//                 //   "formatted_address",
//                 //   "formatted_phone_number",
//                 //   "international_phone_number",
//                 //   "rating",
//                 //   "user_ratings_total",
//                 //   "business_status",
//                 //   "website",
//                 //   "icon",
//                 //   "address_components",
//                 //   "geometry",
//                 //   "vicinity",
//                 //   "types"
//                 // ]
//               //},
//               // (placeResult, placeStatus) => {
//                 if (status === window.google.maps.places.PlacesServiceStatus.OK) {
//                   // Format the details and handle them as needed
//                   return {
//                     place_id: place.place_id,
//                     formatted_address: place.formatted_address,
//                     phone_number: place.formatted_phone_number || place.international_phone_number,
//                     rating: place.rating,
//                     total_ratings: place.user_ratings_total,
//                     business_status: place.business_status,
//                     website: place.website,
//                     icon: place.icon,
//                     address_components: place.address_components,
//                     geometry: place.geometry,
//                     vicinity: place.vicinity,
//                     types: place.types,
//                   };
//                 }
//               }
//             );
//           } else {
//             console.error("No business found or an error occurred:", status);
//           }
//         }
//       );
//     }
//   };

//   const handleBoundsChanged = () => {
//     if (mapRef.current) {
//       const center = mapRef.current.getCenter();
//       const newLat = center.lat();
//       const newLng = center.lng();

//       // Check if the center has moved significantly from the last fetched location
//       if (
//         !lastFetchCenter.lat || 
//         Math.abs(newLat - lastFetchCenter.lat) > FETCH_THRESHOLD || 
//         Math.abs(newLng - lastFetchCenter.lng) > FETCH_THRESHOLD
//       ) {
//         // Update last fetched center
//         setLastFetchCenter({ lat: newLat, lng: newLng });
//         setLatitude(newLat);
//         setLongitude(newLng);

//         // Fetch new businesses
//         fetchNearbyBusinesses({ lat: newLat, lng: newLng });
//       }
//     }
//   };

//   // const fetchNearbyBusinesses = (location) => {
//   //   if (location && mapRef.current) {
//   //     const service = new window.google.maps.places.PlacesService(mapRef.current);
//   //     service.nearbySearch(
//   //       {
//   //         location,
//   //         radius: 1000,
//   //         type: [professionalType.googleTypeName], // Adjust the type as needed
//   //       },
//   //       (results, status) => {
//   //         
//   //         if (status === window.google.maps.places.PlacesServiceStatus.OK) {
//   //           setBusinesses(results);
//   //           // Fetch detailed information for each business
//   //           results.forEach((business) => {
//   //             service.getDetails(
//   //               { placeId: business.place_id },
//   //               (placeResult, placeStatus) => {
//   //                 if (placeStatus === window.google.maps.places.PlacesServiceStatus.OK) {
//   //                   
//   //                   setDetailedBusinesses((prevDetails) => [...prevDetails, placeResult]);
//   //                 }
//   //               }
//   //             );
//   //           });
//   //         } else {
//   //           setBusinesses([]);
//   //         }
//   //       }
//   //     );
//   //   }
//   // };
//   // const mergeRegisteredAndMapBusinesses = () => {
//   //   // Filter out registered businesses from the detailed list
//   //   const filteredDetailedBusinesses = detailedBusinesses.filter((business) => {
//   //     return !registeredProfessionals.some(
//   //       (professional) => professional.reference === business.plus_code?.global_code ||
//   //         professional.place_id === business.place_id
//   //     );
//   //   });

//   //   // Combine registered professionals with the remaining detailed businesses
//   //   const finalBusinesses = [
//   //     ...registeredProfessionals.map((professional) => ({
//   //       ...professional,
//   //       isRegistered: true, // Mark as registered
//   //       professionalData: professional
//   //     })),
//   //     ...filteredDetailedBusinesses.map((business) => ({
//   //       ...business,
//   //       isRegistered: false, // Mark as not registered
//   //       professionalData: null
//   //     }))
//   //   ];
//   //   setMergedBusinesses(finalBusinesses);
//   // };

//   const mergedBusinesses = [
//     ...registeredProfessionals.map((professional) => ({
//       ...professional,
//       isRegistered: true,  // Mark as registered
//       professionalData: professional || null
//     })),...detailedBusinesses
//     // .filter((business) => 
//     //                             !registeredProfessionals.some((professional) => professional.reference === business.plus_code?.global_code 
//        //                                                      || professional.place_id === business.place_id))
//       ]
//           .map((business) => {
//                   if(business.place_id==null && business.plus_code?.global_code == null && business.address != null)
//                    {
//                      var businessobject = searchBusinessByAddress(business.address);

//                     if(businessobject != null)
//                     {
//                       return {
//                         place_id: businessobject.place_id,
//                         formatted_address: businessobject.formatted_address,
//                         phone_number: businessobject.formatted_phone_number || businessobject.international_phone_number,
//                         rating: businessobject.rating,
//                         total_ratings: businessobject.user_ratings_total,
//                         business_status: businessobject.business_status,
//                         website: businessobject.website,
//                         icon: businessobject.icon,
//                         address_components: businessobject.address_components,
//                         geometry: businessobject.geometry,
//                         vicinity: businessobject.vicinity,
//                         types: businessobject.types,
//                         isRegistered: true, // true if there's a match, false otherwise
//                         professionalData: business || null,
//                       };
//                     }
//                     else
//                     {
//                       return {
//                         ...business,
//                         isRegistered: true, // true if there's a match, false otherwise
//                         professionalData: business || null,
//                       };
//                     }

//                   }

//                     // If we're iterating over registeredProfessionals directly, we adjust the property to match
//                     const businessId = business.place_id || business.plus_code?.global_code;

//                     // Find a matching professional in `registeredProfessionals` by `reference`
//                     const matchedProfessional = registeredProfessionals.find(
//                       (professional) => professional.reference === business.plus_code?.global_code
//                     );

//                   if(Boolean(matchedProfessional))
//                   {
//                     return {
//                       ...matchedProfessional,
//                       place_id: business.place_id,
//                       formatted_address: business.formatted_address,
//                       phone_number: business.formatted_phone_number || business.international_phone_number,
//                       rating: business.rating,
//                       total_ratings: business.user_ratings_total,
//                       business_status: business.business_status,
//                       website: business.website,
//                       icon: business.icon,
//                       address_components: business.address_components,
//                       geometry: business.geometry,
//                       vicinity: business.vicinity,
//                       types: business.types,
//                       isRegistered: true, // true if there's a match, false otherwise
//                       professionalData: matchedProfessional || null,
//                     };
//                   }
//                   else
//                   {
//                     return {
//                     name: business.name,
//                     place_id: business.place_id,
//                     formatted_address: business.formatted_address,
//                     phone_number: business.formatted_phone_number || business.international_phone_number,
//                     rating: business.rating,
//                     total_ratings: business.user_ratings_total,
//                     business_status: business.business_status,
//                     website: business.website,
//                     icon: business.icon,
//                     address_components: business.address_components,
//                     geometry: business.geometry,
//                     vicinity: business.vicinity,
//                     types: business.types,
//                     distanceFromCurrent: business.distanceFromCurrent,
//                     // location: business.location,
//                     isRegistered: Boolean(matchedProfessional),
//                     professionalData: matchedProfessional || null,

//                       // ...business,
//                       // isRegistered: Boolean(matchedProfessional), // true if there's a match, false otherwise
//                       // professionalData: matchedProfessional || null,
//                     };
//                   }

//                   });  


//   const handlePlaceSelect = () => {
//     if (autocomplete) {
//       const place = autocomplete.getPlaces()[0];
//       if (place) {
//         setSearchAddress(place.formatted_address);
//         setLatitude(place.geometry.location.lat());
//         setLongitude(place.geometry.location.lng());
//         fetchNearbyBusinesses({ lat: latitude, lng: longitude });;//place.geometry.location
//       } else {
//         setBusinesses([]);
//       }
//     }
//   };

//   const onLoad = (ref) => {
//     setAutocomplete(ref);
//   };

//   const mapOnLoad = (map) => {
//     mapRef.current = map;
//     setMap(map);
//     fetchNearbyBusinesses({ lat: latitude, lng: longitude });

//   };

//   const mapOptions = {
//                     styles: [
//                       {
//                         featureType: "all",
//                         elementType: "geometry.fill",
//                         stylers: [{ weight: "2.00" }],
//                       },
//                       {
//                         featureType: "all",
//                         elementType: "geometry.stroke",
//                         stylers: [{ color: "#e0e0e0" }],
//                       },
//                       {
//                         featureType: "all",
//                         elementType: "labels.text",
//                         stylers: [{ visibility: "on" }, { color: "#444" }],
//                       },
//                       {
//                         featureType: "landscape",
//                         elementType: "geometry.fill",
//                         stylers: [{ color: "#f7f7f7" }],
//                       },
//                       {
//                         featureType: "poi",
//                         elementType: "geometry.fill",
//                         stylers: [{ color: "#eaeaea" }],
//                       },
//                       {
//                         featureType: "road",
//                         elementType: "geometry",
//                         stylers: [{ color: "#ffffff" }],
//                       },
//                       {
//                         featureType: "water",
//                         elementType: "geometry.fill",
//                         stylers: [{ color: "#cce5ff" }],
//                       },
//                     ],
//                     disableDefaultUI: false,
//                     zoomControl: true,
//                     fullscreenControl: false,
//                     streetViewControl: false,
//                   };
//   // const mapOptions = {
//   //   zoomControl: true,
//   //   mapTypeControl: false,
//   //   streetViewControl: false,
//   //   fullscreenControl: false,
//   //   draggableCursor: "pointer",
//   // };

//   const handleMarkerClick = (marker) => {
//     
//     setSelectedMarker(marker);
//   };

//   const handleInfoWindowClose = () => {
//     setSelectedMarker(null);
//   };

//   const handleListItemClick = (professional) => {
//     
//     setFocusedMarker(professional); 
//   };


//   return  isLoaded ? (
//     <Grid container spacing={2} className="choose-professionals-wrapper"  style={{ overflowY: 'auto', maxHeight: '100%', height: '100%',backgroundColor: 'red' }}>
//       <Grid item md={4} className="professional-list-wrapper" style={{ overflowY: 'auto', maxHeight: '100%', height: '100%',backgroundColor: 'red' }}>
//       {mergedBusinesses && mergedBusinesses.length > 0 ? (
//           <ul className="professional-list">
//             {mergedBusinesses.map((professional, index) => (
//               <li
//                       key={index}
//                       className="professional-item"
//                       onClick={() => handleListItemClick(professional)}
//                       style={{
//                         border: '1px solid #ddd',
//                         borderRadius: '12px',
//                         padding: '16px',
//                         marginBottom: '16px',
//                         display: 'flex',
//                         backgroundColor: '#fff',
//                         cursor: 'pointer',
//                         boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
//                       }}
//                     >
//                       {/* Image Section */}
//                       <div style={{ position: 'relative', minWidth: '100px', maxWidth: '120px', marginRight: '16px' }}>
//                         <img
//                           src={professional.imageUrl || "/placeholder.jpg"}
//                           alt={professional.name}
//                           style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
//                         />
//                         <span
//                           style={{
//                             position: 'absolute',
//                             top: '8px',
//                             left: '8px',
//                             backgroundColor: professional.isRegistered ? '#4CAF50' : '#f44336',
//                             color: '#fff',
//                             padding: '2px 8px',
//                             fontSize: '12px',
//                             borderRadius: '8px'
//                           }}
//                         >
//                           {professional.isRegistered ? t("AppointmentForm.Bookable") : t("AppointmentForm.NotAvailable")}
//                         </span>
//                       </div>

//                       {/* Content Section */}
//                       <div style={{ flexGrow: 1, position: 'relative' }}>
//                         {/* Top right distance */}
//                         <span style={{ position: 'absolute', top: 0, right: 0, fontSize: '12px', color: '#555' }}>
//                           {professional?.distanceFromCurrent?.crowflies}
//                         </span>

//                         <h4 style={{ margin: '0 0 4px 0', fontWeight: '600' }}>{professional.name}</h4>
//                         <p style={{ margin: 0, color: '#777' }}>{professional.address || professional.vicinity}</p>
//                         {professional.description && (
//                           <p style={{ marginTop: '8px', fontSize: '14px', color: '#444' }}>{professional.description}</p>
//                         )}

//                         {/* Rating */}
//                         {professional.rating && (
//                           <div style={{ marginTop: '8px' }}>
//                             <Rate value={professional.rating} disabled allowHalf />
//                           </div>
//                         )}

//                         {/* Button */}
//                         <div style={{ marginTop: '12px' }}>
//                           <Button
//                             variant="contained"
//                             color="primary"
//                             className={professional.isRegistered ? "view-schedule-button" : "not-available-button"}
//                             onClick={(e) => {
//                               e.stopPropagation(); // Avoid triggering parent click
//                               handleViewScheduleClick(professional.isRegistered ? professional.professionalData : null);
//                             }}
//                           >
//                             {professional.isRegistered
//                               ? t("AppointmentForm.ViewDisponibilities")
//                               : t("AppointmentForm.NotAvailable")}
//                           </Button>
//                         </div>

//                         {/* Reviews */}
//                         {professional.reviews && professional.reviews.length > 0 && (
//                           <ul className="professional-reviews" style={{ marginTop: '12px' }}>
//                             {professional.reviews.slice(0, 2).map((review, reviewIndex) => (
//                               <li key={reviewIndex} className="professional-review" style={{ fontSize: '13px', color: '#555' }}>
//                                 <p style={{ margin: 0 }}>{review.text}</p>
//                                 <p style={{ margin: 0 }}>Rating: {review.rating} / 5</p>
//                               </li>
//                             ))}
//                           </ul>
//                         )}
//                       </div>
//                     </li>

//               // <li key={index} className="professional-item" onClick={() => handleListItemClick(professional)}>
//               //   <div className="professional-details">
//               //     <h4 className="professional-name">{professional.name}</h4>
//               //     <h5 className="professional-address">{professional.address}</h5>
//               //     <h5 className="professional-distancefromcurrent">{professional?.distanceFromCurrent?.crowflies}</h5>
//               //     <h5 className="professional-address">{professional.vicinity}</h5>
//               //     {professional.rating && (<Rate
//               //                                 value={professional.rating}
//               //                                 disabled
//               //                                 allowHalf
//               //                               />)}
//               //         {professional.reviews && professional.reviews.length > 0 && (
//               //           <ul className="professional-reviews">
//               //             {professional.reviews.map((review, reviewIndex) => (
//               //               <li key={reviewIndex} className="professional-review">
//               //                 <p>{review.text}</p>
//               //                 <p>Rating: {review.rating} / 5</p>
//               //               </li>
//               //             ))}
//               //           </ul>
//               //         )}
//               //     </div>

//               //   {professional.isRegistered ? (
//               //       <Button
//               //           variant="contained"
//               //           color="primary"
//               //           className="view-schedule-button"
//               //           onClick={() => handleViewScheduleClick(professional.professionalData)}
//               //         >
//               //           {t("AppointmentForm.ViewDisponibilities")}
//               //       </Button>
//               //   ):(                
//               //       <Button
//               //         variant="contained"
//               //         color="primary"
//               //         className="not-available-button"
//               //         onClick={() => handleViewScheduleClick(null)}
//               //       >
//               //         {t("AppointmentForm.NotAvailable")}
//               //       </Button>
//               //   )}

//               // </li>
//             ))}
//           </ul>
//         ) : (
//           <Typography variant="h6" style={{ color: 'white' }}>{t("No professionals found in your area.")}</Typography>
//         )}


//       </Grid>
//       {/* googleMapsApiKey="AIzaSyB_j8NP_ZfkMFGHNxqsCL_pPhEIBlBp9As" #f0f0f0*/}
//       <Grid item md={4} className="professionals-map-wrapper" style={{  margin: 0,   display: 'flex',  flexDirection: 'column',
//          borderRadius: '12px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', padding: 0 }} >
//           <div className="search-box-container">
//             <StandaloneSearchBox onLoad={onLoad} onPlacesChanged={handlePlaceSelect}>
//               <div>
//                 <PlaceRounded fontSize="small" htmlColor="#01b075" className="search-location-icon" />
//                 <input type="text" placeholder="Enter address" className="search-input" />
//               </div>
//             </StandaloneSearchBox>
//           </div>
//           <GoogleMap 
//             mapContainerStyle={{ height: "80%", width: "100%", borderRadius: "12px"}}
//             center={{ lat: latitude, lng: longitude }}
//             zoom={16}
//             options={mapOptions}
//             onLoad={mapOnLoad}
//             // onBoundsChanged={handleBoundsChanged} // Trigger search on map bounds change
//           >
//                 {/* <Marker position={{ lat: latitude, lng: longitude }} 
//                 icon={"http://maps.google.com/mapfiles/ms/icons/green-dot.png"} /> */}
//                   {mergedBusinesses && mergedBusinesses.length > 0 && mergedBusinesses.map((professional, index) => (
//                       professional.isRegistered ? (
//                               <Marker
//                               key={index}
//                               position={{ lat: professional.coordinate?.lat, lng: professional.coordinate?.lng }}
//                               icon={focusedMarker && focusedMarker.id === professional.id ? 
//                                 "http://maps.google.com/mapfiles/ms/icons/red-dot.png" : 
//                                 "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"} // Highlight the selected marker in red

//                               onClick={() => handleMarkerClick(professional)}
//                             />
//                             // label={(index + 1).toString()}
//                       ):(     
//                         // <div key={index}>{professional.displayName}</div> 
//                               <Marker
//                               key={index}
//                               position={{ lat: professional.geometry.lat(), lng: professional.geometry.lng() }}
//                               icon={focusedMarker && focusedMarker.id === professional.id ? 
//                                 "http://maps.google.com/mapfiles/ms/icons/red-dot.png" : 
//                                 "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png"}

//                               onClick={() => handleMarkerClick(professional)}
//                             />
//                             // label={(index + 1).toString()}
//                       )
//                   ))}
//                   {/* {detailedBusinesses.map((business, index) => (
//                     <Marker
//                       key={business.place_id}
//                       position={{ lat: business.geometry.location.lat(), lng: business.geometry.location.lng() }}
//                       icon={"http://maps.google.com/mapfiles/ms/icons/red-dot.png"}
//                       label={(index + 1 + registeredProfessionals.length).toString()}
//                       onClick={() => handleMarkerClick(business)}
//                     />
//                   ))} */}
//                   {selectedMarker && (
//                     <InfoWindow
//                       position={{
//                         lat: selectedMarker.geometry ? selectedMarker.geometry.location.lat() : selectedMarker.coordinate.lat,
//                         lng: selectedMarker.geometry ? selectedMarker.geometry.location.lng() : selectedMarker.coordinate.lng,
//                       }}
//                       onCloseClick={handleInfoWindowClose}
//                     >
//                       {selectedMarker.isRegistered  ? (
//                               <div className="info-window">
//                                     <h4>{selectedMarker.name}</h4>
//                                     <p>{selectedMarker.vicinity || selectedMarker.address}</p>
//                                     <button onClick={() => handleViewScheduleClick(selectedMarker)}>
//                                       {t("AppointmentForm.ViewDisponibilities")}
//                                     </button>
//                             </div>
//                       ):(                
//                         <div className="info-window">
//                                 <h4>{selectedMarker.name}</h4>
//                                 <p>{selectedMarker.vicinity || selectedMarker.address}</p>
//                                 <button>
//                                     {t("AppointmentForm.NotAvailable")}
//                                 </button>
//                         </div>
//                       )}
//                     </InfoWindow>
//                   )}
//           </GoogleMap> 
//         {/* </LoadScript> */}
//       </Grid>
//     </Grid>
//   ): (
//     <div>Loading...</div>
//   );
// };

// export default MapPicker;
// detailedPlaces.forEach((place) => {
//   const marker = new AdvancedMarkerElement({
//     map,
//     position: place.geometry,
//     title: place.name,
//   });
//   bounds.extend(place.geometry);
// });

// map.fitBounds(bounds);
// if (status === window.google.maps.places.PlacesServiceStatus.OK) {
// setDetailedBusinesses(places);
// } 
// setDetailedBusinesses(places);

// service.nearbySearch(
//   {
//     location,
//     radius: 1000,
//     bounds: bounds,
//     type: [professionalType.googleTypeName], // Adjust the type as needed
//   },
//   (results, status) => {
//     if (status === window.google.maps.places.PlacesServiceStatus.OK) {

//        
//       setDetailedBusinesses(results);
//     }
//   }
// );

{/* {registeredProfessionals && registeredProfessionals.length > 0 ? (
          <ul className="professional-list">
            {registeredProfessionals.map((professional, index) => (
              <li key={professional.id} className="professional-item">
                <div className="professional-details">
                  <h4 className="professional-name">{index + 1} - {professional.name}</h4>
                  <h5 className="professional-address">{professional.address}</h5>
                  <h5 className="professional-distancefromcurrent">{professional?.distanceFromCurrent?.crowflies}</h5>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  className="view-schedule-button"
                  onClick={() => handleViewScheduleClick(professional)}
                >
                  {t("AppointmentForm.ViewDisponibilities")}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <Typography variant="h6" style={{ color: 'white' }}>{t("No professionals found in your area.")}</Typography>
        )}
        {detailedBusinesses && detailedBusinesses.length > 0 && (
          <ul className="professional-list">
            {detailedBusinesses.map((business, index) => (
              <li key={business.place_id} className="professional-item">
                <div className="professional-details">
                  <h4 className="professional-name">{index + 1 + registeredProfessionals.length} - {business.name}</h4>
                  <h5 className="professional-address">{business.vicinity}</h5>
                  <Rate
                    value={business.rating}
                    disabled
                    allowHalf
                  />
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  className="not-available-button"
                  onClick={() => handleViewScheduleClick(null)}
                >
                  {t("AppointmentForm.NotAvailable")}
                </Button>
              </li>
            ))}
          </ul>
        )} */}